import { useEffect, useState, type ComponentType } from 'react';
import { AlertTriangle } from 'lucide-react';
import QuizPanel, { type QuizConfig, type QuizQuestion } from './components/quiz/QuizPanel';
import Flashcard, { type FlashcardItem } from './components/widgets/Flashcard';
import DragDropMatcher, { type MatchPair } from './components/widgets/DragDropMatcher';
import EquipmentWidget from './components/widgets/EquipmentWidget';
import LabBench from './components/experiments/LabBench';
import ExperimentRunner, { type ExperimentConfig } from './components/experiments/ExperimentRunner';
import { Beaker, Burette, PHMeter, Spectrophotometer } from './components/lab';
import {
  InteractivePeriodicTable,
  ParticleView,
  BohrAtom,
  FormulaBreakdown,
  PercentComposition,
  type PeriodicTableConfig,
  type ParticleViewConfig,
  type BohrAtomConfig,
  type FormulaBreakdownConfig,
  type PercentCompositionConfig,
} from './components/viz';
// College-level interactive widget clusters (built as separate registries to
// avoid edit conflicts). Merged into one lookup used in the default case.
import { ATOMIC_WIDGETS } from './components/viz/atomic';
import { BONDING_WIDGETS } from './components/viz/bonding';
import { REACTIONS_WIDGETS } from './components/viz/reactions';
import { SOLUTIONS_WIDGETS } from './components/viz/solutions';
import { RICH_WIDGETS } from './components/viz/rich';
// The two widgets that implement the WIP-016 state-capture contract (design
// §8.3) are imported directly, ahead of the cluster registries above, so
// `onStateChange` can be forwarded with a real prop type instead of widening
// `CLUSTER_WIDGETS`' shared component type for every other cluster widget.
// This is the ONLY change either widget's own file makes for this feature —
// a new optional prop neither widget calls unless a caller passes it, so
// every existing caller (the lecture player, the Virtual Lab gallery) is
// unaffected.
import LewisStructure, { type LewisStructureConfig } from './components/viz/bonding/LewisStructure';
import EquationBalancer, { type EquationBalancerConfig } from './components/viz/reactions/EquationBalancer';

// Narration-synced cue forwarded from the lecture player. Every field is
// optional and a widget may ignore the whole object — widgets that don't read
// `cue` keep working unchanged. Kept in lockstep with the lecture player's
// transitions.WidgetCue.
export interface WidgetCue {
  progress?: number; // 0–1 of the slide's narration elapsed
  highlight?: string | string[];
  step?: number;
  play?: boolean;
}

const CLUSTER_WIDGETS: Record<
  string,
  ComponentType<{ config: any; onComplete?: () => void; cue?: WidgetCue }>
> = {
  ...ATOMIC_WIDGETS,
  ...BONDING_WIDGETS,
  ...REACTIONS_WIDGETS,
  ...SOLUTIONS_WIDGETS,
  ...RICH_WIDGETS,
};

// THE integration point the lecture player imports. Maps widget.type to a
// component and renders it, normalising config defensively so a partial or
// malformed config never crashes the player. onComplete is invoked when the
// activity is finished — this drives the player's advance-when-covered gating.

// Kept structurally compatible with the lecture player's WidgetRef (optional
// fields + index signature) so a slide's widget_ref is assignable verbatim.
export interface Widget {
  id?: string;
  type?: string;
  config?: Record<string, unknown> | null;
  [key: string]: unknown;
}

// WIP-016 (design §8.3) — a serialisable snapshot a participating widget
// reports on every meaningful change: `state` is what the answer is (opaque
// to everything except the widget itself and whatever eventually grades it —
// untrusted client input, validated server-side, never interpreted here) and
// `display` is the short human-readable form an instructor scans in a
// 77-row table (design's 200-character cap is enforced where the payload is
// built, not here — this type only describes the shape).
export interface WidgetStateSnapshot {
  state: unknown;
  display: string;
}

// Widget `type`s that implement the contract above (call `onStateChange`).
// Every id here MUST exist in `widgetCatalog.ts` — the catalogue's own
// "renders live" test fails if one doesn't, which is the guard against a
// step referencing a widget that no longer exists. Not every widget is
// answerable this way: one absent here falls back to the ordinary
// choice/number/text controls, per design §8.3.
export const WIDGET_ANSWER_TYPES: ReadonlySet<string> = new Set(['equation_balancer', 'lewis_structure']);

interface Props {
  widget: Widget;
  onComplete?: (result?: unknown) => void;
  // Narration-synced cue from the lecture player. Optional + backward-compatible:
  // forwarded to every widget; a widget that ignores it still works.
  cue?: WidgetCue;
  /** WIP-016 — reports the widget's latest state, for a step whose answer
   *  IS the widget's own state (design §8.3). Only forwarded to the widgets
   *  in `WIDGET_ANSWER_TYPES`; every other widget never receives it. */
  onStateChange?: (snapshot: WidgetStateSnapshot) => void;
}

// ── config normalisers (lenient: accept several shapes, fall back to []/{}) ────

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

export function normalizeQuestions(raw: any[]): QuizQuestion[] {
  return raw.map((q: any) => {
    const kind = q?.kind ?? q?.type ?? 'mcq';
    const choices = asArray<any>(q?.choices).map((c: any, i: number) =>
      typeof c === 'string'
        ? { id: String.fromCharCode(97 + i), text: c }
        : { id: c?.id ?? String.fromCharCode(97 + i), text: c?.text ?? String(c) },
    );
    // Resolve `correct` to the matching choice id for graded choice questions.
    // Authors write it three ways — a 0-based index, the choice's text, or an
    // explicit id — and the grader compares the picked choice's id, so all three
    // must collapse to that id. Numeric/short-answer `correct` is left untouched.
    let correct = q?.correct ?? q?.correctOptionId;
    if (kind !== 'numeric' && choices.length) {
      if (typeof correct === 'number' && choices[correct]) {
        correct = choices[correct].id;
      } else if (typeof correct === 'string') {
        const hit = choices.find((c) => c.id === correct) ?? choices.find((c) => c.text === correct);
        if (hit) correct = hit.id; // else leave as-is (e.g. short-answer keyword)
      }
    }
    return {
      id: q?.id,
      kind,
      prompt: q?.prompt ?? q?.question ?? '',
      choices,
      correct,
      tolerance: q?.tolerance,
      unit: q?.unit,
      explanation: q?.explanation,
      points: q?.points,
    };
  });
}

function toQuizConfig(config: Record<string, unknown>, questions?: any[]): QuizConfig {
  // Questions can arrive either inline in config (config.questions / bare array)
  // or as a sibling `questions` array (the server widget shape).
  const raw =
    questions && questions.length
      ? questions
      : Array.isArray(config)
        ? config
        : asArray<QuizQuestion>(config.questions);
  return { title: (config.title as string) ?? 'Quiz', questions: normalizeQuestions(raw) };
}

function toFlashcards(config: Record<string, unknown>): FlashcardItem[] {
  return asArray<any>(config.cards ?? config.flashcards).map((c: any) => ({
    front: c?.front ?? c?.term ?? '',
    back: c?.back ?? c?.definition ?? '',
  }));
}

function toMatchPairs(config: Record<string, unknown>): MatchPair[] {
  return asArray<any>(config.pairs ?? config.items).map((p: any) => ({
    term: p?.term ?? p?.left ?? '',
    match: p?.match ?? p?.right ?? p?.definition ?? '',
  }));
}

// Re-type a widget component so it also accepts the optional narration `cue`,
// without editing the widget's own Props. The consuming side adds `cue` to each
// widget over time; until then `withCue(Comp)` lets us forward it type-safely at
// the call site. It's a pure type-level widening (identity at runtime) — a
// widget that ignores `cue` is unaffected; one that reads it gets it.
function withCue<P>(Comp: ComponentType<P>): ComponentType<P & { cue?: WidgetCue }> {
  return Comp as ComponentType<P & { cue?: WidgetCue }>;
}

// Cue-aware aliases for the widgets the lecture player drives. Built once at
// module load so the JSX below stays clean.
const EquipmentWidgetC = withCue(EquipmentWidget);
const ExperimentRunnerC = withCue(ExperimentRunner);
const PeriodicTableC = withCue(InteractivePeriodicTable);
const ParticleViewC = withCue(ParticleView);
const BohrAtomC = withCue(BohrAtom);
const FormulaBreakdownC = withCue(FormulaBreakdown);
const PercentCompositionC = withCue(PercentComposition);

function Unsupported({ type }: { type: string }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-ws-border bg-ws-surface p-4 text-sm text-ws-ink-2">
      <AlertTriangle size={18} className="flex-shrink-0 text-ws-warning" />
      <span>
        Unsupported widget type <code className="rounded bg-ws-surface-2 px-1.5 py-0.5 text-xs">{type}</code>.
      </span>
    </div>
  );
}

// Exported BOTH named and default: the lecture player's SlideStage imports the
// named `WidgetRenderer`; the lab barrel re-exports the default. The callers'
// `onComplete?: () => void` is assignment-compatible with our optional-arg form.
export function WidgetRenderer({ widget, onComplete, cue, onStateChange }: Props) {
  const config = (widget?.config ?? {}) as Record<string, unknown>;
  const type = widget?.type ?? 'unknown';

  // Some widget configs may carry a lessonId to lazily fetch quiz questions; that
  // path is handled inline below for the quiz case. `cue` is forwarded to every
  // widget below; consumers that don't read it are unaffected.
  switch (type) {
    // ── WIP-016 state-capture contract (design §8.3) — checked ahead of the
    // cluster default below so `onStateChange` reaches a real, typed prop.
    // Both otherwise behave exactly as they do via the cluster registries.
    case 'lewis_structure':
      return (
        <LewisStructure
          config={config as LewisStructureConfig}
          onComplete={onComplete}
          cue={cue}
          onStateChange={onStateChange}
        />
      );

    case 'equation_balancer':
      return (
        <EquationBalancer
          config={config as EquationBalancerConfig}
          onComplete={onComplete}
          cue={cue}
          onStateChange={onStateChange}
        />
      );

    case 'quiz':
      return <QuizWidget config={config} onComplete={onComplete} />;

    case 'flashcard':
      return (
        <Flashcard
          cards={toFlashcards(config)}
          title={config.title as string | undefined}
          onComplete={() => onComplete?.({ type: 'flashcard', done: true })}
        />
      );

    case 'drag_drop':
      return (
        <DragDropMatcher
          pairs={toMatchPairs(config)}
          title={config.title as string | undefined}
          onComplete={() => onComplete?.({ type: 'drag_drop', done: true })}
        />
      );

    case 'lab_beaker':
    case 'lab_burette':
    case 'ph_meter':
    case 'spectrophotometer':
      return <EquipmentWidgetC kind={type} config={config} onComplete={onComplete} cue={cue} />;

    case 'lab_bench':
      return (
        <LabBench
          experimentName={(config.name as string) ?? 'Lab Bench'}
          completedCount={(config.completedCount as number) ?? 0}
          totalSteps={(config.totalSteps as number) ?? 0}
        >
          <div className="flex flex-wrap items-end justify-center gap-10">
            <Beaker fillLevel={40} label="Sample" />
            <Burette volume={50} />
            <PHMeter reading={7} />
            <Spectrophotometer />
          </div>
        </LabBench>
      );

    case 'experiment':
      return (
        <ExperimentRunnerC
          config={config as ExperimentConfig}
          onComplete={(r) => onComplete?.({ type: 'experiment', ...r })}
          cue={cue}
        />
      );

    // ── Interactive chemistry visualizations (SVG + animation) ────────────────
    case 'periodic_table':
      return <PeriodicTableC config={config as PeriodicTableConfig} onComplete={onComplete} cue={cue} />;

    case 'particle_view':
      return <ParticleViewC config={config as ParticleViewConfig} onComplete={onComplete} cue={cue} />;

    case 'bohr_atom':
      return <BohrAtomC config={config as BohrAtomConfig} onComplete={onComplete} cue={cue} />;

    case 'chemical_formula':
      return <FormulaBreakdownC config={config as FormulaBreakdownConfig} onComplete={onComplete} cue={cue} />;

    case 'percent_composition':
      return <PercentCompositionC config={config as PercentCompositionConfig} onComplete={onComplete} cue={cue} />;

    default: {
      // College-level cluster widgets (atomic / bonding / reactions / solutions).
      const Cluster = CLUSTER_WIDGETS[type];
      if (Cluster) return <Cluster config={config} onComplete={onComplete} cue={cue} />;
      return <Unsupported type={type} />;
    }
  }
}

// Quiz needs its own small wrapper: if the config has no inline questions but
// carries a lessonId, fetch the lesson's quiz widgets and use the first one's
// questions. Falls back to whatever inline config is present.
function QuizWidget({
  config,
  onComplete,
}: {
  config: Record<string, unknown>;
  onComplete?: (result: unknown) => void;
}) {
  const [quiz, setQuiz] = useState<QuizConfig>(() => toQuizConfig(config));
  const lessonId = config.lessonId as string | undefined;
  const hasInline = quiz.questions.length > 0;

  useEffect(() => {
    if (hasInline || !lessonId) return;
    let cancelled = false;
    (async () => {
      try {
        const { fetchLessonWidgets } = await import('./services/labApi');
        const widgets = await fetchLessonWidgets(lessonId);
        const quizWidget = widgets.find((w) => w.type === 'quiz');
        if (!cancelled && quizWidget) setQuiz(toQuizConfig(quizWidget.config, quizWidget.questions));
      } catch {
        /* leave the (empty) inline quiz; QuizPanel renders an empty state */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasInline, lessonId]);

  return (
    <QuizPanel
      quiz={quiz}
      onComplete={(r) => onComplete?.({ type: 'quiz', ...r })}
    />
  );
}

export default WidgetRenderer;
